/**
 * Courses API
 */
import apiClient from './client';

export interface LectureResponse {
  lecture_id: string;
  title: string;
  course_id: string;
  video_path: string;
  duration: number;
  order: number;
  description: string;
  thumbnail_path?: string;
  uploaded_at: string;
  summary?: string;
  topics?: string[];
  hidden?: boolean;
}

export interface RAGProcessingStatus {
  is_ready: boolean;
  total_lectures: number;
  processed_lectures: number;
  status: 'ready' | 'processing' | 'not_started' | 'partial' | 'error';
  message: string;
  progress_percentage: number;
}

export interface CourseResponse {
  course_id: string;
  title: string;
  instructor: string;
  instructor_name?: string;
  description: string;
  thumbnail_path?: string;
  created_at: string;
  lectures: LectureResponse[];
  lecture_count: number;
  school_id: string;
  semester?: string;
  year?: number;
  rag_status?: RAGProcessingStatus;
  // Course home page details
  welcome_message?: string;
  class_schedule?: string;
  class_meeting_link?: string;
  office_hours?: string;
  office_hours_link?: string;
  contact_email?: string;
  class_location?: string;
  meeting_days?: string[];
  meeting_start?: string;
  meeting_end?: string;
  course_start?: string;
  course_end?: string;
}

export interface CourseCreateRequest {
  title: string;
  description?: string;
  thumbnail_path?: string;
  semester?: string;
  year?: number;
}

export interface CourseUpdateRequest {
  title?: string;
  description?: string;
  thumbnail_path?: string;
  semester?: string;
  year?: number;
  // Course home page details
  welcome_message?: string;
  class_schedule?: string;
  class_meeting_link?: string;
  office_hours?: string;
  office_hours_link?: string;
  contact_email?: string;
  class_location?: string;
  meeting_days?: string[];
  meeting_start?: string;
  meeting_end?: string;
  course_start?: string;
  course_end?: string;
}

export interface LectureCreateRequest {
  title: string;
  course_id: string;
  description?: string;
  order?: number;
}

export interface VideoUploadResponse {
  success: boolean;
  message: string;
  lecture_id: string;
  lecture: LectureResponse;
}

export interface EnrollmentResponse {
  success: boolean;
  message: string;
  course_id: string;
  student_username: string;
  enrolled_at: string;
}

export interface ProgressUpdateRequest {
  watched_duration: number;
  total_duration: number;
  position: number;
}

export interface ProgressResponse {
  lecture_id: string;
  watched_duration: number;
  total_duration: number;
  position: number;
  completed: boolean;
  progress_percent: number;
  last_updated: string;
}

export interface SlideResponse {
  slide_id: string;
  title: string;
  course_id: string;
  file_path: string;
  file_name: string;
  file_size: number;
  uploaded_at: string;
  hidden?: boolean;
}

export interface SlideUploadResponse {
  success: boolean;
  message: string;
  slide_id: string;
  slide: SlideResponse;
}

/**
 * Create a new course (teacher only)
 */
export const createCourse = async (data: CourseCreateRequest): Promise<CourseResponse> => {
  const response = await apiClient.post<CourseResponse>('/courses', data);
  return response.data;
};

/**
 * Get all courses (with optional filters)
 */
export const getCourses = async (filters?: {
  instructor?: string;
  search?: string;
  semester?: string;
  year?: number;
}): Promise<CourseResponse[]> => {
  const params: Record<string, string | number> = {};
  if (filters?.instructor) params.instructor = filters.instructor;
  if (filters?.search) params.search = filters.search;
  if (filters?.semester) params.semester = filters.semester;
  if (filters?.year) params.year = filters.year;
  const response = await apiClient.get<{ courses: CourseResponse[]; total: number }>('/courses', { params });
  return response.data.courses;
};

/**
 * Get course by ID
 */
export const getCourse = async (courseId: string): Promise<CourseResponse> => {
  const response = await apiClient.get<CourseResponse>(`/courses/${courseId}`);
  return response.data;
};

/**
 * Update course (teacher only)
 */
export const updateCourse = async (
  courseId: string,
  data: CourseUpdateRequest
): Promise<CourseResponse> => {
  const response = await apiClient.put<CourseResponse>(`/courses/${courseId}`, data);
  return response.data;
};

/**
 * Delete course (teacher only)
 */
export const deleteCourse = async (courseId: string): Promise<void> => {
  await apiClient.delete(`/courses/${courseId}`);
};

// Cloudflare rejects request bodies over 100 MB, so large files are sent in chunks
const UPLOAD_CHUNK_SIZE = 50 * 1024 * 1024;
const CHUNK_RETRIES = 3;

/**
 * Send file to `${baseUrl}/chunk` in pieces, then POST `fields` to
 * `${baseUrl}/complete` so the server joins them. onUploadProgress
 * receives { loaded, total } for the whole file.
 */
const uploadInChunks = async <T,>(
  baseUrl: string,
  file: File,
  fields: Record<string, string>,
  onUploadProgress?: (progressEvent: any) => void
): Promise<T> => {
  const uploadId = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  const totalChunks = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE));

  for (let index = 0; index < totalChunks; index++) {
    const start = index * UPLOAD_CHUNK_SIZE;
    const blob = file.slice(start, Math.min(start + UPLOAD_CHUNK_SIZE, file.size));

    for (let attempt = 1; ; attempt++) {
      const formData = new FormData();
      formData.append('upload_id', uploadId);
      formData.append('chunk_index', String(index));
      formData.append('chunk', blob, `${index}.part`);

      try {
        await apiClient.post(`${baseUrl}/chunk`, formData, {
          headers: {
            'Content-Type': undefined, // Let browser set multipart boundary automatically
          },
          onUploadProgress: (ev: any) => {
            const chunkLoaded = Math.min(ev.loaded ?? 0, blob.size);
            onUploadProgress?.({ loaded: start + chunkLoaded, total: file.size });
          },
          timeout: 600000,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        });
        break;
      } catch (err: any) {
        const code = err.response?.status;
        // Client errors (auth, bad input) will not succeed on retry
        if (attempt >= CHUNK_RETRIES || (code && code >= 400 && code < 500)) {
          throw err;
        }
      }
    }
  }

  const formData = new FormData();
  formData.append('upload_id', uploadId);
  formData.append('total_chunks', String(totalChunks));
  formData.append('filename', file.name);
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));

  const response = await apiClient.post<T>(`${baseUrl}/complete`, formData, {
    headers: {
      'Content-Type': undefined,
    },
    timeout: 600000, // joining + post-processing for large files
  });
  return response.data;
};

/**
 * Upload video and create lecture (sent in chunks, see uploadInChunks)
 */
export const uploadLecture = async (
  courseId: string,
  title: string,
  description: string,
  videoFile: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<VideoUploadResponse> =>
  uploadInChunks<VideoUploadResponse>(
    `/courses/${courseId}/lectures/upload`,
    videoFile,
    { title, description },
    onUploadProgress
  );

/**
 * Get lectures in a course
 */
export const getCourseLectures = async (courseId: string): Promise<LectureResponse[]> => {
  const response = await apiClient.get<LectureResponse[]>(`/courses/${courseId}/lectures`);
  return response.data;
};

/**
 * Get lecture by ID
 */
export const getLecture = async (lectureId: string): Promise<LectureResponse> => {
  const response = await apiClient.get<LectureResponse>(`/lectures/${lectureId}`);
  return response.data;
};

/**
 * Delete lecture (teacher only)
 */
export const deleteLecture = async (lectureId: string): Promise<void> => {
  await apiClient.delete(`/lectures/${lectureId}`);
};

/**
 * Enroll in a course (student only)
 */
export const enrollInCourse = async (courseId: string): Promise<EnrollmentResponse> => {
  const response = await apiClient.post<EnrollmentResponse>(`/courses/${courseId}/enroll`, {
    course_id: courseId,
  });
  return response.data;
};

/**
 * Get enrolled courses (student) or created courses (teacher)
 */
export const getEnrolledCourses = async (): Promise<CourseResponse[]> => {
  const response = await apiClient.get<{ courses: CourseResponse[]; total: number }>('/courses/my-courses');
  return response.data.courses;  // Extract courses array from CourseListResponse
};

/**
 * Update lecture progress (student)
 */
export const updateLectureProgress = async (
  lectureId: string,
  data: ProgressUpdateRequest
): Promise<ProgressResponse> => {
  const response = await apiClient.put<ProgressResponse>(`/progress/lectures/${lectureId}`, data);
  return response.data;
};

/**
 * Get lecture progress (student)
 */
export const getLectureProgress = async (lectureId: string): Promise<ProgressResponse> => {
  const response = await apiClient.get<ProgressResponse>(`/progress/lectures/${lectureId}`);
  return response.data;
};

/**
 * Upload a slide/presentation to a course (teacher only, sent in chunks)
 */
export const uploadSlide = async (
  courseId: string,
  title: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<SlideUploadResponse> =>
  uploadInChunks<SlideUploadResponse>(
    `/courses/${courseId}/slides/upload`,
    file,
    { title },
    onUploadProgress
  );

/**
 * Get slides for a course
 */
export const getCourseSlides = async (courseId: string): Promise<SlideResponse[]> => {
  const response = await apiClient.get<SlideResponse[]>(`/courses/${courseId}/slides`);
  return response.data;
};

/**
 * Delete a slide (teacher only)
 */
export const deleteSlide = async (slideId: string): Promise<void> => {
  await apiClient.delete(`/slides/${slideId}`);
};

// ============== Recommended Videos ==============

export interface RecommendedVideoResponse {
  video_id: string;
  title: string;
  course_id: string;
  youtube_url: string;
  youtube_video_id: string;
  added_by: string;
  added_at: string;
  processing_status: 'not_processed' | 'processing' | 'processed' | 'error';
  has_transcript: boolean;
  hidden?: boolean;
}

export const getCourseRecommendedVideos = async (courseId: string): Promise<RecommendedVideoResponse[]> => {
  const response = await apiClient.get<RecommendedVideoResponse[]>(`/courses/${courseId}/recommended-videos`);
  return response.data;
};

export const addRecommendedVideo = async (
  courseId: string,
  title: string,
  youtubeUrl: string
): Promise<RecommendedVideoResponse> => {
  const response = await apiClient.post<RecommendedVideoResponse>(
    `/courses/${courseId}/recommended-videos`,
    { title, youtube_url: youtubeUrl }
  );
  return response.data;
};

export const deleteRecommendedVideo = async (videoId: string): Promise<void> => {
  await apiClient.delete(`/recommended-videos/${videoId}`);
};

export const processRecommendedVideo = async (videoId: string): Promise<RecommendedVideoResponse> => {
  const response = await apiClient.post<RecommendedVideoResponse>(`/recommended-videos/${videoId}/process`);
  return response.data;
};

// ============== Quizzes ==============

export interface QuizQuestionOption {
  label: string;
  text: string;
}

export interface QuizQuestionRubricDimension {
  name: string;
  beginning: string;
  developing: string;
  proficient: string;
}

export interface QuizQuestionRubric {
  dimensions: QuizQuestionRubricDimension[];
  exemplar_response: string;
  key_concepts: string[];
}

export interface QuizQuestion {
  question_id: string;
  question_text: string;
  question_type?: 'mcq' | 'open_ended';
  options?: QuizQuestionOption[];
  correct_answer?: string;
  explanation?: string;
  rubric?: QuizQuestionRubric;
  points?: number;
}

export interface QuizResponse {
  quiz_id: string;
  course_id: string;
  title: string;
  created_by: string;
  created_at: string;
  source_lectures: string[];
  source_slides: string[];
  source_files?: string[];
  num_questions: number;
  questions: QuizQuestion[];
  published: boolean;
  available_from?: string | null;
  due_at?: string | null;
  max_attempts?: number | null;
}

export interface QuizListItem {
  quiz_id: string;
  course_id: string;
  title: string;
  created_by: string;
  created_at: string;
  num_questions: number;
  published: boolean;
  available_from?: string | null;
  due_at?: string | null;
  attempts_count?: number;
  pending_review?: number;
  my_attempted?: boolean;
  my_attempts?: number;
  max_attempts?: number | null;
}

export interface QuizUpdateRequest {
  title?: string;
  published?: boolean;
  questions?: QuizQuestion[];
  available_from?: string;
  due_at?: string;
  clear_available_from?: boolean;
  clear_due_at?: boolean;
  max_attempts?: number;
}

export interface QuizAttemptResult {
  question_id: string;
  question_text: string;
  question_type?: 'mcq' | 'open_ended';
  selected?: string;
  correct?: string;
  is_correct?: boolean | null;
  explanation: string;
  response_text?: string;
  rubric?: QuizQuestionRubric;
  points?: number;
  awarded?: number;
  adjusted?: boolean;
}

export interface QuizAttemptResponse {
  attempt_id: string;
  quiz_id: string;
  student_username: string;
  score: number;
  total: number;
  percentage: number;
  open_ended_count?: number;
  completed_at: string;
  results: QuizAttemptResult[];
}

export const getCourseQuizzes = async (courseId: string): Promise<QuizListItem[]> => {
  const response = await apiClient.get<QuizListItem[]>(`/courses/${courseId}/quizzes`);
  return response.data;
};

export interface GenerateQuizParams {
  title: string;
  lectureIds?: string[];
  slideIds?: string[];
  recommendedVideoIds?: string[];
  fileIds?: string[];
  numQuestions: number;
  openEndedCount?: number;
  bloomLevels?: number[];
  difficulty?: 'easy' | 'medium' | 'hard';
  maxAttempts?: number;
}

export const generateQuiz = async (
  courseId: string,
  params: GenerateQuizParams,
): Promise<QuizResponse> => {
  const body: Record<string, unknown> = {
    title: params.title,
    lecture_ids: params.lectureIds ?? [],
    slide_ids: params.slideIds ?? [],
    recommended_video_ids: params.recommendedVideoIds ?? [],
    file_ids: params.fileIds ?? [],
    num_questions: params.numQuestions,
    open_ended_count: params.openEndedCount ?? 0,
    max_attempts: params.maxAttempts ?? 1,
  };
  if (params.bloomLevels?.length) body.bloom_levels = params.bloomLevels;
  if (params.difficulty) body.difficulty = params.difficulty;
  const response = await apiClient.post<QuizResponse>(`/courses/${courseId}/quizzes/generate`, body, {
    timeout: 600000, // 10 minutes — batched quiz generation for large question counts
  });
  return response.data;
};

export interface QuizGenerationJob {
  job_id: string;
  course_id: string;
  title: string;
  created_by: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  completed_questions: number;
  total_questions: number;
  quiz_id?: string | null;
  error?: string | null;
  created_at: string;
  updated_at: string;
}

export const startQuizGeneration = async (
  courseId: string,
  params: GenerateQuizParams,
): Promise<QuizGenerationJob> => {
  const body: Record<string, unknown> = {
    title: params.title,
    lecture_ids: params.lectureIds ?? [],
    slide_ids: params.slideIds ?? [],
    recommended_video_ids: params.recommendedVideoIds ?? [],
    file_ids: params.fileIds ?? [],
    num_questions: params.numQuestions,
    open_ended_count: params.openEndedCount ?? 0,
    max_attempts: params.maxAttempts ?? 1,
  };
  if (params.bloomLevels?.length) body.bloom_levels = params.bloomLevels;
  if (params.difficulty) body.difficulty = params.difficulty;
  const response = await apiClient.post<QuizGenerationJob>(`/courses/${courseId}/quizzes/generate-async`, body);
  return response.data;
};

export const getQuizGenerationJob = async (courseId: string, jobId: string): Promise<QuizGenerationJob> => {
  const response = await apiClient.get<QuizGenerationJob>(`/courses/${courseId}/quizzes/generation-jobs/${jobId}`);
  return response.data;
};

export const listQuizGenerationJobs = async (courseId: string): Promise<QuizGenerationJob[]> => {
  const response = await apiClient.get<QuizGenerationJob[]>(`/courses/${courseId}/quizzes/generation-jobs`);
  return response.data;
};

export const getQuiz = async (quizId: string): Promise<QuizResponse> => {
  const response = await apiClient.get<QuizResponse>(`/quizzes/${quizId}`);
  return response.data;
};

export const deleteQuiz = async (quizId: string): Promise<void> => {
  await apiClient.delete(`/quizzes/${quizId}`);
};

export const updateQuiz = async (
  quizId: string,
  data: QuizUpdateRequest
): Promise<QuizResponse> => {
  const response = await apiClient.patch<QuizResponse>(`/quizzes/${quizId}`, data);
  return response.data;
};

export const submitQuiz = async (
  quizId: string,
  answers: Record<string, string>
): Promise<QuizSubmitAck> => {
  const response = await apiClient.post<QuizSubmitAck>(`/quizzes/${quizId}/submit`, { answers });
  return response.data;
};

// ============== Scaffolds (Bloom's Taxonomy) ==============

export interface ScaffoldMetrics {
  cognitive_monotonicity: number;
  scaffold_coherence: number;
  scaffold_diversity: number;
  scaffold_completeness: number;
  type_transition_score: number;
  ccs_trajectory: number[];
}

export interface ScaffoldRubricDimension {
  name: string;
  description: string;
  beginning: string;
  developing: string;
  proficient: string;
}

export interface ScaffoldRubric {
  dimensions: ScaffoldRubricDimension[];
  exemplar_response: string;
  key_concepts: string[];
}

export interface ScaffoldQuestion {
  question_id: string;
  question_text: string;
  bloom_level: number;
  bloom_level_label: string;
  question_type: 'mcq' | 'open_ended';
  scaffold_position: number;
  options?: QuizQuestionOption[];
  correct_answer?: string;
  rubric?: ScaffoldRubric;
  explanation: string;
  bloom_confidence: number;
  ccs_score?: number;
}

export interface ScaffoldResponse {
  scaffold_id: string;
  course_id: string;
  title: string;
  topic: string;
  questions: ScaffoldQuestion[];
  bloom_distribution: Record<string, number>;
  type_distribution: Record<string, number>;
  source_lectures: string[];
  created_by: string;
  created_at: string;
  published: boolean;
  condition_id: string;
  metrics?: ScaffoldMetrics;
  num_questions: number;
}

export interface ScaffoldListItem {
  scaffold_id: string;
  title: string;
  topic: string;
  bloom_distribution: Record<string, number>;
  type_distribution: Record<string, number>;
  source_lectures: string[];
  source_files?: string[];
  created_by: string;
  created_at: string;
  published: boolean;
  num_questions: number;
  condition_id: string;
  metrics?: ScaffoldMetrics;
}

export interface ScaffoldGenerateRequest {
  title: string;
  lecture_ids: string[];
  slide_ids: string[];
  file_ids?: string[];
  topic?: string;
  preset: string;
}

export const getCourseScaffolds = async (courseId: string): Promise<ScaffoldListItem[]> => {
  const response = await apiClient.get<ScaffoldListItem[]>(`/courses/${courseId}/scaffolds`);
  return response.data;
};

export const generateScaffold = async (
  courseId: string,
  request: ScaffoldGenerateRequest
): Promise<ScaffoldResponse> => {
  const response = await apiClient.post<ScaffoldResponse>(
    `/courses/${courseId}/scaffolds/generate`,
    { ...request, condition: 'D_full_scaffold' },
    { timeout: 600000 }
  );
  return response.data;
};

export interface ScaffoldGenerationJob {
  job_id: string;
  course_id: string;
  title: string;
  topic: string;
  created_by: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  scaffold_id?: string | null;
  error?: string | null;
}

export const startScaffoldGenerationJob = async (
  courseId: string,
  request: ScaffoldGenerateRequest,
): Promise<ScaffoldGenerationJob> => {
  const response = await apiClient.post<ScaffoldGenerationJob>(
    `/courses/${courseId}/scaffolds/generate-async`,
    { ...request, condition: 'D_full_scaffold' },
  );
  return response.data;
};

export const extractFileTopics = async (courseId: string, fileId: string): Promise<string[]> => {
  const response = await apiClient.post<{ topics: string[] }>(
    `/courses/${courseId}/files/${fileId}/topics/extract`,
    {},
    { timeout: 180000 },
  );
  return response.data.topics;
};

export const getScaffoldGenerationJob = async (
  courseId: string,
  jobId: string,
): Promise<ScaffoldGenerationJob> => {
  const response = await apiClient.get<ScaffoldGenerationJob>(
    `/courses/${courseId}/scaffolds/generation-jobs/${jobId}`,
  );
  return response.data;
};

export const getScaffold = async (scaffoldId: string): Promise<ScaffoldResponse> => {
  const response = await apiClient.get<ScaffoldResponse>(`/scaffolds/${scaffoldId}`);
  return response.data;
};

export const updateScaffold = async (
  scaffoldId: string,
  data: { title?: string; published?: boolean }
): Promise<ScaffoldResponse> => {
  const response = await apiClient.patch<ScaffoldResponse>(`/scaffolds/${scaffoldId}`, data);
  return response.data;
};

export const deleteScaffold = async (scaffoldId: string): Promise<void> => {
  await apiClient.delete(`/scaffolds/${scaffoldId}`);
};

export interface ScaffoldPublishAsQuizResponse {
  success: boolean;
  quiz_id: string;
  title: string;
  num_questions: number;
  mcq_count: number;
  open_ended_count: number;
  message: string;
}

export const publishScaffoldsAsQuiz = async (
  courseId: string,
  scaffoldIds: string[],
  title: string,
): Promise<ScaffoldPublishAsQuizResponse> => {
  const response = await apiClient.post<ScaffoldPublishAsQuizResponse>(
    `/courses/${courseId}/scaffolds/publish-as-quiz`,
    { scaffold_ids: scaffoldIds, title },
  );
  return response.data;
};

// ============== Lecture Notes ==============

export interface LectureNotesResponse {
  lecture_id: string;
  course_id: string;
  lecture_title: string;
  content: string;
  generated_by: string;
  generated_at: string;
}

export const generateLectureNotes = async (lectureId: string): Promise<void> => {
  await apiClient.post(`/lectures/${lectureId}/notes/generate`);
};

export const getLectureNotes = async (lectureId: string): Promise<LectureNotesResponse | null> => {
  const response = await apiClient.get<LectureNotesResponse | null>(`/lectures/${lectureId}/notes`);
  return response.data;
};

// Topic Extraction
export const extractLectureTopics = async (lectureId: string): Promise<string[]> => {
  const response = await apiClient.post<{ topics: string[]; lecture_id: string }>(`/lectures/${lectureId}/topics/extract`);
  return response.data.topics;
};

// ============== Reflection Analysis ==============

export interface ReflectionResponse {
  reflection_id: string;
  lecture_id: string;
  course_id: string;
  student_username: string;
  interesting_topics: string[];
  challenging_topics: string[];
  submitted_at: string;
  interesting_comment?: string;
  challenging_comment?: string;
}

export interface ReflectionAnalyticsResponse {
  lecture_id: string;
  lecture_title: string;
  course_id: string;
  total_responses: number;
  interesting_counts: Record<string, number>;
  challenging_counts: Record<string, number>;
  comments?: { student_username: string; interesting_comment: string; challenging_comment: string; submitted_at: string }[];
}

export const submitReflection = async (
  lectureId: string,
  interestingTopics: string[],
  challengingTopics: string[],
  interestingComment = '',
  challengingComment = '',
): Promise<ReflectionResponse> => {
  const response = await apiClient.post<ReflectionResponse>(`/lectures/${lectureId}/reflections`, {
    interesting_topics: interestingTopics,
    challenging_topics: challengingTopics,
    interesting_comment: interestingComment,
    challenging_comment: challengingComment,
  });
  return response.data;
};

export const getMyReflection = async (lectureId: string): Promise<ReflectionResponse | null> => {
  const response = await apiClient.get<ReflectionResponse | null>(`/lectures/${lectureId}/reflections/me`);
  return response.data;
};

export const getReflectionAnalytics = async (lectureId: string): Promise<ReflectionAnalyticsResponse> => {
  const response = await apiClient.get<ReflectionAnalyticsResponse>(`/lectures/${lectureId}/reflections/analytics`);
  return response.data;
};

// ============== Course-Level Reflection Analytics ==============

export interface LectureReflectionSummary {
  lecture_id: string;
  lecture_title: string;
  total_responses: number;
  interesting_counts: Record<string, number>;
  challenging_counts: Record<string, number>;
}

export interface CourseReflectionAnalyticsResponse {
  course_id: string;
  course_title: string;
  total_responses: number;
  lectures_with_responses: number;
  total_lectures: number;
  interesting_counts: Record<string, number>;
  challenging_counts: Record<string, number>;
  lecture_analytics: LectureReflectionSummary[];
}

export const getCourseReflectionAnalytics = async (
  courseId: string
): Promise<CourseReflectionAnalyticsResponse> => {
  const response = await apiClient.get<CourseReflectionAnalyticsResponse>(
    `/courses/${courseId}/reflections/analytics`
  );
  return response.data;
};

// ============== Enrolled Students ==============

export interface EnrolledStudentResponse {
  username: string;
  full_name: string;
  email: string;
  enrolled_at: string;
}

export interface EnrolledStudentsListResponse {
  course_id: string;
  total_students: number;
  students: EnrolledStudentResponse[];
}

export const getCourseStudents = async (
  courseId: string
): Promise<EnrolledStudentsListResponse> => {
  const response = await apiClient.get<EnrolledStudentsListResponse>(
    `/courses/${courseId}/students`
  );
  return response.data;
};

// ============== Watch Progress (Continue Watching) ==============

export interface WatchProgress {
  lecture_id: string;
  course_id: string;
  position_seconds: number;
  duration_seconds: number;
  percent: number;
  updated_at: string;
}

export interface ContinueWatchingItem extends WatchProgress {
  lecture_title: string;
  course_title: string;
}

/** Save the current user's playback position for a lecture. */
export const saveWatchProgress = async (
  lectureId: string,
  positionSeconds: number,
  durationSeconds: number
): Promise<WatchProgress> => {
  const response = await apiClient.put<WatchProgress>(`/lectures/${lectureId}/watch-progress`, {
    position_seconds: positionSeconds,
    duration_seconds: durationSeconds,
  });
  return response.data;
};

/** Get the current user's saved playback position for a lecture. */
export const getMyWatchProgress = async (lectureId: string): Promise<WatchProgress | null> => {
  const response = await apiClient.get<WatchProgress | null>(`/lectures/${lectureId}/watch-progress/me`);
  return response.data;
};

/** Recently watched, unfinished lectures for the current user. */
export const getContinueWatching = async (): Promise<ContinueWatchingItem[]> => {
  const response = await apiClient.get<ContinueWatchingItem[]>('/lectures/continue-watching/me');
  return response.data;
};


// ============== Upcoming quizzes (countdown / reminders) ==============

export interface UpcomingQuizItem {
  quiz_id: string;
  course_id: string;
  course_title: string;
  title: string;
  available_from?: string | null;
  due_at?: string | null;
  status: 'scheduled' | 'open';
  next_deadline: string;
  item_type?: 'quiz' | 'assignment';
}

/** Published quizzes with future openings or deadlines across the user's courses. */
export const getUpcomingQuizzes = async (): Promise<UpcomingQuizItem[]> => {
  const response = await apiClient.get<UpcomingQuizItem[]>('/quizzes/upcoming/me');
  return response.data;
};

// ============== Content Folders and Course Files (D2L-style materials) ==============

export interface ContentFolder {
  folder_id: string;
  course_id: string;
  name: string;
  created_at: string;
  file_count: number;
  hidden?: boolean;
}

export interface CourseFile {
  file_id: string;
  course_id: string;
  folder_id?: string | null;
  title: string;
  file_path: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
  hidden?: boolean;
  kind?: 'file' | 'link';
  url?: string | null;
  text_status?: 'pending' | 'ready' | 'unsupported' | 'failed' | null;
  text_chars?: number;
  text_error?: string | null;
  topics?: string[] | null;
}

export const createContentFolder = async (courseId: string, name: string): Promise<ContentFolder> => {
  const response = await apiClient.post<ContentFolder>(`/courses/${courseId}/folders`, { name });
  return response.data;
};

export const listContentFolders = async (courseId: string): Promise<ContentFolder[]> => {
  const response = await apiClient.get<ContentFolder[]>(`/courses/${courseId}/folders`);
  return response.data;
};

export const renameContentFolder = async (courseId: string, folderId: string, name: string): Promise<ContentFolder> => {
  const response = await apiClient.patch<ContentFolder>(`/courses/${courseId}/folders/${folderId}`, { name });
  return response.data;
};

export const deleteContentFolder = async (courseId: string, folderId: string): Promise<void> => {
  await apiClient.delete(`/courses/${courseId}/folders/${folderId}`);
};

/** Upload any file type as course material, optionally into a folder. */
export const uploadCourseFile = async (
  courseId: string,
  file: File,
  options?: { title?: string; folderId?: string; onUploadProgress?: (progressEvent: any) => void }
): Promise<CourseFile> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.title) formData.append('title', options.title);
  if (options?.folderId) formData.append('folder_id', options.folderId);
  const response = await apiClient.post<CourseFile>(`/courses/${courseId}/files/upload`, formData, {
    headers: { 'Content-Type': undefined },
    onUploadProgress: options?.onUploadProgress,
    timeout: 600000,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });
  return response.data;
};

export const addCourseLink = async (
  courseId: string,
  data: { title?: string; url: string; folderId?: string | null },
): Promise<CourseFile> => {
  const response = await apiClient.post<CourseFile>(`/courses/${courseId}/links`, {
    title: data.title ?? '',
    url: data.url,
    folder_id: data.folderId ?? '',
  });
  return response.data;
};

export const listCourseFiles = async (courseId: string): Promise<CourseFile[]> => {
  const response = await apiClient.get<CourseFile[]>(`/courses/${courseId}/files`);
  return response.data;
};

export const deleteCourseFile = async (courseId: string, fileId: string): Promise<void> => {
  await apiClient.delete(`/courses/${courseId}/files/${fileId}`);
};


/** Instructor: show or hide a content item for students. */
export const setContentVisibility = async (
  courseId: string,
  itemType: 'lecture' | 'slide' | 'recommended_video' | 'folder' | 'file',
  itemId: string,
  hidden: boolean
): Promise<void> => {
  await apiClient.patch(`/courses/${courseId}/content-visibility`, {
    item_type: itemType,
    item_id: itemId,
    hidden,
  });
};

// ============== Grades ==============

export interface StudentGradeItem {
  quiz_id: string;
  title: string;
  num_questions: number;
  due_at?: string | null;
  attempts_count: number;
  best_percentage?: number | null;
  last_attempt_at?: string | null;
  overridden?: boolean;
  pending?: boolean;
  feedback?: string;
  item_type?: 'quiz' | 'assignment';
}

export interface StudentGrades {
  items: StudentGradeItem[];
  average?: number | null;
}

export interface GradebookQuiz {
  quiz_id: string;
  title: string;
  due_at?: string | null;
}

export interface GradebookRow {
  username: string;
  full_name: string;
  scores: Record<string, number | null>;
  overridden?: Record<string, boolean>;
  average?: number | null;
}

export interface Gradebook {
  quizzes: GradebookQuiz[];
  rows: GradebookRow[];
  quiz_averages: Record<string, number | null>;
  class_average?: number | null;
}

/** A student's per-quiz grades in a course. */
export const getMyGrades = async (courseId: string): Promise<StudentGrades> => {
  const response = await apiClient.get<StudentGrades>(`/courses/${courseId}/grades/me`);
  return response.data;
};

/** Instructor gradebook: enrolled students x published quizzes. */
export const getGradebook = async (courseId: string): Promise<Gradebook> => {
  const response = await apiClient.get<Gradebook>(`/courses/${courseId}/gradebook`);
  return response.data;
};


/** Instructor: adjust a student's grade on a quiz; pass null to revert to the earned score. */
export const setGradeOverride = async (
  courseId: string,
  username: string,
  quizId: string,
  percentage: number | null
): Promise<void> => {
  await apiClient.put(`/courses/${courseId}/grades/${username}`, {
    quiz_id: quizId,
    percentage,
  });
};

// ============== Grade review and release (quizzes) ==============

export interface QuizSubmitAck {
  attempt_id: string;
  quiz_id: string;
  submitted: boolean;
  open_ended_count: number;
  message: string;
}

export interface QuizAttemptSummary {
  attempt_id: string;
  quiz_id: string;
  student_username: string;
  full_name: string;
  completed_at: string;
  score: number;
  total: number;
  percentage: number;
  open_ended_count: number;
  released: boolean;
  feedback: string;
}

export interface QuizAttemptDetail extends QuizAttemptSummary {
  earned_points: number;
  total_points: number;
  results: QuizAttemptResult[];
}

/** Instructor: all attempts on a quiz. */
export const getQuizAttempts = async (quizId: string): Promise<QuizAttemptSummary[]> => {
  const response = await apiClient.get<QuizAttemptSummary[]>(`/quizzes/${quizId}/attempts`);
  return response.data;
};

/** Instructor: one attempt with per-question responses. */
export const getQuizAttemptDetail = async (attemptId: string): Promise<QuizAttemptDetail> => {
  const response = await apiClient.get<QuizAttemptDetail>(`/quizzes/attempts/${attemptId}`);
  return response.data;
};

/** Instructor: set feedback and/or release state for one student's quiz grade. */
export const reviewStudentGrade = async (
  courseId: string,
  username: string,
  quizId: string,
  data: { feedback?: string; released?: boolean }
): Promise<void> => {
  await apiClient.put(`/courses/${courseId}/grades/${username}/review`, {
    quiz_id: quizId,
    ...data,
  });
};

/** Instructor: release grades to every student who attempted the quiz. */
export const releaseQuizGrades = async (quizId: string): Promise<{ released_count: number }> => {
  const response = await apiClient.post<{ released_count: number }>(`/quizzes/${quizId}/release`);
  return response.data;
};

// ============== Assignments ==============

export interface AssignmentAttachment {
  file_id: string;
  title: string;
  file_path: string;
  file_name: string;
  file_size: number;
  content_type: string;
}

export interface AssignmentResponse {
  assignment_id: string;
  course_id: string;
  title: string;
  instructions: string;
  attachments: AssignmentAttachment[];
  available_from?: string | null;
  due_at?: string | null;
  published: boolean;
  created_at: string;
  submission_count: number;
  pending_review?: number;
  my_submitted?: boolean;
}

export interface AssignmentSubmission {
  submission_id: string;
  assignment_id: string;
  course_id: string;
  student_username: string;
  full_name: string;
  files: AssignmentAttachment[];
  comment: string;
  submitted_at: string;
  submitted_late: boolean;
  grade_percentage?: number | null;
  feedback: string;
  released: boolean;
}

export const createAssignment = async (
  courseId: string,
  data: { title: string; instructions?: string; available_from?: string; due_at?: string }
): Promise<AssignmentResponse> => {
  const response = await apiClient.post<AssignmentResponse>(`/courses/${courseId}/assignments`, data);
  return response.data;
};

export const listAssignments = async (courseId: string): Promise<AssignmentResponse[]> => {
  const response = await apiClient.get<AssignmentResponse[]>(`/courses/${courseId}/assignments`);
  return response.data;
};

export const updateAssignment = async (
  assignmentId: string,
  data: {
    title?: string;
    instructions?: string;
    available_from?: string;
    due_at?: string;
    published?: boolean;
    clear_available_from?: boolean;
    clear_due_at?: boolean;
  }
): Promise<AssignmentResponse> => {
  const response = await apiClient.patch<AssignmentResponse>(`/assignments/${assignmentId}`, data);
  return response.data;
};

export const deleteAssignment = async (assignmentId: string): Promise<void> => {
  await apiClient.delete(`/assignments/${assignmentId}`);
};

/** Instructor: attach a file (any type) to an assignment. */
export const addAssignmentAttachment = async (
  assignmentId: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<AssignmentResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<AssignmentResponse>(
    `/assignments/${assignmentId}/attachments`,
    formData,
    { headers: { 'Content-Type': undefined }, onUploadProgress, timeout: 600000, maxBodyLength: Infinity, maxContentLength: Infinity }
  );
  return response.data;
};

/** Student: submit work (multiple files of any type; resubmission replaces). */
export const submitAssignment = async (
  assignmentId: string,
  files: File[],
  comment?: string,
  onUploadProgress?: (progressEvent: any) => void
): Promise<AssignmentSubmission> => {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  if (comment) formData.append('comment', comment);
  const response = await apiClient.post<AssignmentSubmission>(
    `/assignments/${assignmentId}/submit`,
    formData,
    { headers: { 'Content-Type': undefined }, onUploadProgress, timeout: 600000, maxBodyLength: Infinity, maxContentLength: Infinity }
  );
  return response.data;
};

export const getAssignmentSubmissions = async (assignmentId: string): Promise<AssignmentSubmission[]> => {
  const response = await apiClient.get<AssignmentSubmission[]>(`/assignments/${assignmentId}/submissions`);
  return response.data;
};

export const getMySubmission = async (assignmentId: string): Promise<AssignmentSubmission | null> => {
  const response = await apiClient.get<AssignmentSubmission | null>(`/assignments/${assignmentId}/submissions/me`);
  return response.data;
};

/** Instructor: grade/feedback/release one submission. */
export const gradeSubmission = async (
  submissionId: string,
  data: { grade_percentage?: number | null; feedback?: string; released?: boolean }
): Promise<AssignmentSubmission> => {
  const response = await apiClient.patch<AssignmentSubmission>(`/assignments/submissions/${submissionId}`, data);
  return response.data;
};

/** Instructor: release every graded submission on an assignment. */
export const releaseAssignmentGrades = async (assignmentId: string): Promise<{ released_count: number }> => {
  const response = await apiClient.post<{ released_count: number }>(`/assignments/${assignmentId}/release`);
  return response.data;
};

// ============== Notifications ==============

export interface AppNotification {
  notification_id: string;
  title: string;
  body: string;
  link: string;
  read: boolean;
  created_at: string;
}

export const getMyNotifications = async (unreadOnly = false): Promise<AppNotification[]> => {
  const response = await apiClient.get<AppNotification[]>('/notifications/me', { params: { unread_only: unreadOnly } });
  return response.data;
};

export const markNotificationRead = async (notificationId: string): Promise<void> => {
  await apiClient.patch(`/notifications/${notificationId}/read`);
};

export const markAllNotificationsRead = async (): Promise<void> => {
  await apiClient.post('/notifications/read-all');
};

// ============== Calendar ==============

export interface CalendarEvent {
  id: string;
  type: 'quiz_due' | 'quiz_opens' | 'assignment_due';
  title: string;
  course_id: string;
  course_title: string;
  at: string;
}

export interface CourseMeeting {
  course_id: string;
  course_title: string;
  days: string[];
  start: string;
  end: string;
  location: string;
  schedule_text: string;
  course_start: string;
  course_end: string;
}

export interface CalendarData {
  events: CalendarEvent[];
  meetings: CourseMeeting[];
}

/** Deadlines and weekly class meetings across the user's courses. */
export const getMyCalendar = async (): Promise<CalendarData> => {
  const response = await apiClient.get<CalendarData>('/courses/calendar/me');
  return response.data;
};

// ============== Reflection AI Analysis (Cogni-Reflect pipeline) ==============

export interface ReflectionLectureAnalysis {
  lecture_id: string;
  lecture_title: string;
  lecture_order: number;
  total_responses: number;
  top_interesting: Record<string, number>;
  top_challenging: Record<string, number>;
  summary: string;
  teaching_notes: string;
  generated_at: string;
}

export interface ReflectionStudentAnalysis {
  student_username: string;
  challenging_list: string[];
  interesting_list: string[];
  challenges_analysis: string;
  interesting_analysis: string;
  generated_at: string;
}

export interface ReflectionAnalysisJob {
  status?: 'running' | 'completed' | 'failed';
  progress?: string;
  error?: string;
  finished_at?: string;
}

export interface ReflectionAnalysisResults {
  job: ReflectionAnalysisJob;
  lectures: ReflectionLectureAnalysis[];
  students: ReflectionStudentAnalysis[];
}

export const startReflectionAnalysis = async (courseId: string): Promise<{ status: string }> => {
  const response = await apiClient.post<{ status: string }>(`/courses/${courseId}/reflection-analysis`);
  return response.data;
};

export const getReflectionAnalysis = async (courseId: string): Promise<ReflectionAnalysisResults> => {
  const response = await apiClient.get<ReflectionAnalysisResults>(`/courses/${courseId}/reflection-analysis`);
  return response.data;
};

// ============== Reflection structure, learning paths, outcomes ==============

export interface LearningObjective {
  id: string;
  text: string;
}

export const getReflectionStructure = async (courseId: string): Promise<{ learning_objectives: LearningObjective[] }> => {
  const response = await apiClient.get(`/courses/${courseId}/reflection-structure`);
  return response.data;
};

export const saveReflectionStructure = async (
  courseId: string,
  objectives: LearningObjective[]
): Promise<{ learning_objectives: LearningObjective[] }> => {
  const response = await apiClient.put(`/courses/${courseId}/reflection-structure`, {
    learning_objectives: objectives,
  });
  return response.data;
};

export interface LearningPathConcept {
  topic: string;
  state: 'engaged' | 'flipped' | 'recurring' | 'raised';
  chal_lectures: number[];
  int_lectures: number[];
}

export interface LearningPath {
  student_username: string;
  timeline: {
    lecture_id: string;
    lecture_title: string;
    order: number;
    interesting: string[];
    challenging: string[];
    interesting_comment: string;
    challenging_comment: string;
  }[];
  concepts: LearningPathConcept[];
  summary: {
    engaged: number;
    flipped: number;
    recurring: number;
    raised: number;
    reflected_lectures: number;
    total_lectures: number;
  };
}

export const getLearningPath = async (courseId: string, username: string): Promise<LearningPath> => {
  const response = await apiClient.get(`/courses/${courseId}/students/${username}/learning-path`);
  return response.data;
};

export interface ReflectionOutcomes {
  job: ReflectionAnalysisJob;
  learning_map: {
    objectives: LearningObjective[];
    tree: {
      objective_id: string;
      objective_text: string;
      challenging: number;
      interesting: number;
      lectures: {
        lecture_id: string;
        lecture_title: string;
        order: number;
        challenging: number;
        interesting: number;
        topics: { topic: string; type: 'interesting' | 'challenging'; count: number }[];
      }[];
    }[];
    unmapped: { topic: string; type: string; count: number }[];
    generated_at: string;
  } | null;
  outcomes: {
    students: {
      student_username: string;
      verdict: 'On track' | 'Some gaps' | 'Needs support';
      coverage: number;
      unresolved: number;
      objectives: {
        objective_id: string;
        objective_text: string;
        status: 'strong' | 'developing' | 'unresolved' | 'no-evidence';
        engaged: { topic: string }[];
        resolved: { topic: string }[];
        struggled: { topic: string; state: string }[];
      }[];
      narrative: string;
    }[];
    generated_at: string;
  } | null;
}

export const startReflectionOutcomes = async (courseId: string): Promise<{ status: string }> => {
  const response = await apiClient.post(`/courses/${courseId}/reflection-outcomes`);
  return response.data;
};

export const getReflectionOutcomes = async (courseId: string): Promise<ReflectionOutcomes> => {
  const response = await apiClient.get(`/courses/${courseId}/reflection-outcomes`);
  return response.data;
};

// ============== Reflection heatmap (learning trends) ==============

export interface ReflectionHeatmapCell {
  order: number;
  challenging: number;
  interesting: number;
}

export interface ReflectionHeatmapTopic {
  topic: string;
  total_challenging: number;
  total_interesting: number;
  total: number;
  cells: ReflectionHeatmapCell[];
}

export interface ReflectionHeatmap {
  lectures: { lecture_id: string; title: string; order: number }[];
  topics: ReflectionHeatmapTopic[];
}

export const getReflectionHeatmap = async (courseId: string, top = 20): Promise<ReflectionHeatmap> => {
  const response = await apiClient.get(`/courses/${courseId}/reflection-heatmap`, { params: { top } });
  return response.data;
};

// ============== Course structure (outcomes -> modules -> lectures) ==============

export interface StructureModule {
  module_id: string;
  title: string;
  order: number;
  objective_ids: string[];
  lecture_ids: string[];
}

export interface StructureLecture {
  lecture_id: string;
  title: string;
  order: number;
  reflection_count: number;
}

export interface CourseStructure {
  learning_objectives: LearningObjective[];
  modules: StructureModule[];
  lecture_outcomes: Record<string, string[]>;
  lectures: StructureLecture[];
}

export const getCourseStructure = async (courseId: string): Promise<CourseStructure> => {
  const response = await apiClient.get(`/courses/${courseId}/reflection-structure`);
  return response.data;
};

export const saveCourseStructure = async (
  courseId: string,
  data: {
    learning_objectives: LearningObjective[];
    modules: Omit<StructureModule, 'order'>[];
    lecture_outcomes: Record<string, string[]>;
  }
): Promise<{ learning_objectives: LearningObjective[]; modules: StructureModule[]; lecture_outcomes: Record<string, string[]> }> => {
  const response = await apiClient.put(`/courses/${courseId}/reflection-structure`, data);
  return response.data;
};


/** Instructor: adjust per-question awarded points on an attempt. */
export const setAttemptScores = async (
  attemptId: string,
  scores: Record<string, number>
): Promise<QuizAttemptDetail> => {
  const response = await apiClient.patch<QuizAttemptDetail>(`/quizzes/attempts/${attemptId}/scores`, { scores });
  return response.data;
};

// ============== Admin: activity, logins, users ==============

export interface AdminStats {
  users: Record<string, number>;
  courses: number;
  lectures: number;
  quizzes: number;
  quiz_attempts: number;
  assignments: number;
  assignment_submissions: number;
  reflections: number;
  logins_24h: number;
  logins_7d: number;
  activity_24h: number;
}

export interface ActivityEvent {
  event_id: string;
  username: string;
  action: string;
  detail: string;
  ip: string;
  user_agent: string;
  status: string;
  at: string;
}

export interface LoginMapPoint {
  lat: number;
  lon: number;
  city: string;
  country: string;
  count: number;
  usernames: string[];
}

export interface LoginMap {
  points: LoginMapPoint[];
  recent: { username: string; success: boolean; ip: string; city: string; country: string; at: string }[];
  total: number;
  local_or_unknown: number;
  days: number;
}

export interface AdminUser {
  username: string;
  full_name: string;
  email: string;
  role: 'student' | 'teacher' | 'admin';
  active: boolean;
  school_id: string;
  created_at: string;
  last_login: string;
}

export const getAdminStats = async (): Promise<AdminStats> => {
  const response = await apiClient.get<AdminStats>('/admin/stats');
  return response.data;
};

export const getAdminActivity = async (params?: { limit?: number; username?: string; action?: string }): Promise<ActivityEvent[]> => {
  const response = await apiClient.get<ActivityEvent[]>('/admin/activity', { params });
  return response.data;
};

export const getAdminLoginMap = async (days = 30): Promise<LoginMap> => {
  const response = await apiClient.get<LoginMap>('/admin/logins/map', { params: { days } });
  return response.data;
};

export const getAdminUsers = async (search = ''): Promise<AdminUser[]> => {
  const response = await apiClient.get<AdminUser[]>('/admin/users', { params: { search } });
  return response.data;
};

export const updateAdminUser = async (
  username: string,
  data: { role?: string; active?: boolean; full_name?: string; email?: string }
): Promise<Partial<AdminUser>> => {
  const response = await apiClient.patch(`/admin/users/${username}`, data);
  return response.data;
};

export const adminResetPassword = async (username: string): Promise<{ temp_password: string; message: string }> => {
  const response = await apiClient.post(`/admin/users/${username}/reset-password`);
  return response.data;
};

export const adminDeleteUser = async (username: string): Promise<void> => {
  await apiClient.delete(`/admin/users/${username}`);
};

// ============== Roster import (OneRoster-compatible) ==============

export interface RosterImportRow {
  row: number;
  username?: string;
  email?: string;
  full_name?: string;
  action: 'created' | 'enrolled' | 'skipped' | 'error';
  message?: string;
  emailed?: boolean;
  temp_password?: string;
}

export interface RosterImportResult {
  created: number;
  enrolled: number;
  skipped: number;
  errors: number;
  rows: RosterImportRow[];
}

/** Import a roster CSV (OneRoster users.csv compatible) into a course. */
export const importRoster = async (courseId: string, file: File): Promise<RosterImportResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<RosterImportResult>(
    `/courses/${courseId}/roster/import`,
    formData,
    { headers: { 'Content-Type': undefined }, timeout: 300000 }
  );
  return response.data;
};
